import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarDaysIcon, 
  MagnifyingGlassIcon, 
  CloudArrowUpIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import AppIcon from '../../assets/transcript-app-icon.png';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';
import StatsWidget from '../../components/reusable/StatsWidget';
import ActivityFeed from '../../components/reusable/ActivityFeed';
import { transcriptApi } from '../../api/transcriptApi';
import { eventsApi } from '../../api/eventsApi';
import { formatDuration } from '../../utils/formatDuration';

export default function Home() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalSessions: 0,
    activeSessions: 0,
    completedToday: 0,
    totalEvents: 0,
    totalDuration: 0,
  });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch stats and activities
  useEffect(() => {
    if (!user) return;

    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Fetch sessions
        const sessionsData = await transcriptApi.getSessions({ per_page: 1000 });
        const sessions = Array.isArray(sessionsData?.sessions) 
          ? sessionsData.sessions 
          : (Array.isArray(sessionsData?.results) ? sessionsData.results : []);
        
        // Fetch events
        const eventsData = await eventsApi.getAll({ limit: 1000 });
        const events = eventsData?.events || [];
        
        // Calculate stats
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        const completedToday = sessions.filter(s => {
          const completed = s.processing_completed_at || s.processingCompletedAt;
          return completed && new Date(completed) >= todayStart && s.status === 'COMPLETED';
        }).length;
        
        const activeSessions = sessions.filter(s => 
          s.status === 'IN_PROGRESS' || s.status === 'PROCESSING'
        ).length;
        
        const totalDuration = sessions.reduce((sum, s) => {
          return sum + (s.duration_seconds || s.durationSeconds || 0);
        }, 0);
        
        setStats({
          totalSessions: sessions.length,
          activeSessions,
          completedToday,
          totalEvents: events.length,
          totalDuration: Math.round(totalDuration / 60), // Convert to minutes
        });
        
        // Generate activity feed from recent sessions
        const recentSessions = sessions
          .sort((a, b) => {
            const dateA = new Date(a.created_at || a.createdAt || 0);
            const dateB = new Date(b.created_at || b.createdAt || 0);
            return dateB - dateA;
          })
          .slice(0, 10)
          .map(s => {
            const status = s.status || 'PENDING';
            const type = status === 'COMPLETED' 
              ? 'transcript_completed' 
              : status === 'FAILED' 
              ? 'transcript_failed' 
              : 'session_created';
            
            return {
              id: s.id,
              type,
              message: status === 'COMPLETED' 
                ? `Transcript completed for "${s.title || 'Untitled'}"`
                : status === 'FAILED'
                ? `Transcript failed for "${s.title || 'Untitled'}"`
                : `Session "${s.title || 'Untitled'}" created`,
              timestamp: s.created_at || s.createdAt,
              duration_seconds: s.duration_seconds || s.durationSeconds,
              link: `/sessions/${s.id}`,
            };
          });
        
        setActivities(recentSessions);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  // Refresh page when user successfully logs in
  if (!user) {
    return (
      <LoginForm 
        onSSOLogin={() => {}} // Will be handled by parent
        ssoLoading={false}
        ssoError={null}
      />
    );
  }

  const handleNavigation = (route) => {
    navigate(route);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Hero Section - Material Design Style */}
        <div className="mb-12 md:mb-16">
          <div className="relative">
            {/* Subtle gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-indigo-50/50 rounded-2xl"></div>
            
            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 lg:gap-12">
              {/* Left side - Logo only */}
              <div className="flex-shrink-0">
                <img src={AppIcon} alt="Transcript App" className="h-16 md:h-20 w-auto" />
              </div>

              {/* Right side - Quick Actions */}
              {!loading && (
                <div className="flex-1 w-full lg:w-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-4">
                    {/* View Events Card */}
                    <button
                      onClick={() => handleNavigation('/events')}
                      className="group bg-white/80 backdrop-blur-sm rounded-lg p-5 border border-gray-200/50 hover:bg-white hover:shadow-md transition-all duration-200 text-left w-full"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-blue-100 rounded-lg p-2.5 flex-shrink-0">
                          <CalendarDaysIcon className="h-6 w-6 text-blue-600" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">
                          View Events
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Browse through all available events and conferences. Find transcripts, sessions, and related materials.
                      </p>
                      <div className="text-sm font-medium text-blue-600 group-hover:text-blue-700">
                        Browse Events →
                      </div>
                    </button>

                    {/* Search Sessions Card */}
                    <button
                      onClick={() => handleNavigation('/sessions')}
                      className="group bg-white/80 backdrop-blur-sm rounded-lg p-5 border border-gray-200/50 hover:bg-white hover:shadow-md transition-all duration-200 text-left w-full"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-green-100 rounded-lg p-2.5 flex-shrink-0">
                          <MagnifyingGlassIcon className="h-6 w-6 text-green-600" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">
                          Search Sessions
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Search through transcripts and sessions using keywords, speakers, or specific content.
                      </p>
                      <div className="text-sm font-medium text-green-600 group-hover:text-green-700">
                        Search Sessions →
                      </div>
                    </button>

                    {/* Upload New Card */}
                    <button
                      onClick={() => handleNavigation('/upload')}
                      className="group bg-white/80 backdrop-blur-sm rounded-lg p-5 border border-gray-200/50 hover:bg-white hover:shadow-md transition-all duration-200 text-left w-full"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-purple-100 rounded-lg p-2.5 flex-shrink-0">
                          <CloudArrowUpIcon className="h-6 w-6 text-purple-600" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">
                          Upload New
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Upload new transcript files, audio recordings, or other materials for processing.
                      </p>
                      <div className="text-sm font-medium text-purple-600 group-hover:text-purple-700">
                        Upload Files →
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics Dashboard */}
        {!loading && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatsWidget
                title="Total Sessions"
                value={stats.totalSessions}
                subtitle="All time"
                icon={DocumentTextIcon}
                iconColor="blue"
                link="/sessions"
              />
              <StatsWidget
                title="Active Processing"
                value={stats.activeSessions}
                subtitle="In progress"
                icon={ClockIcon}
                iconColor="orange"
                link="/sessions?status=IN_PROGRESS"
              />
              <StatsWidget
                title="Completed Today"
                value={stats.completedToday}
                subtitle="Last 24 hours"
                icon={CheckCircleIcon}
                iconColor="green"
                link="/sessions?status=COMPLETED"
              />
              <StatsWidget
                title="Total Events"
                value={stats.totalEvents}
                subtitle="All events"
                icon={CalendarDaysIcon}
                iconColor="purple"
                link="/events"
              />
            </div>
            
            {/* Activity Feed and Additional Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <ActivityFeed activities={activities} maxItems={8} />
                </div>
              </div>
              <div className="space-y-6">
                <StatsWidget
                  title="Total Duration"
                  value={`${stats.totalDuration} min`}
                  subtitle="Processed transcripts"
                  icon={ChartBarIcon}
                  iconColor="indigo"
                />
              </div>
            </div>

          </div>
        )}

        {loading && (
          <div className="mb-12 text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        )}

        
      </div>
    </div>
  );
}
